// src/components/forms/shared/BookingSummary.tsx

import React, { useMemo } from 'react';
import { Box, Typography, useTheme, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Pet, KennelSideOption } from '../../../services/api';
import SummaryRow from './SummaryRow';
import { SelectedPet } from '../helpers/groomingHelpers';
import { Kennel, KennelSelection } from '../helpers/kennelHelpers';

interface Surcharge {
  id: number;
  reason: string;
  amount: number;
}

interface SystemSettings {
  id: number;
  heaterDailyRate: number;
}

interface BookingSummaryProps {
  checkInDate: string;
  checkOutDate: string;
  numberOfDays: number;
  selectedPets: SelectedPet[];
  availablePets: Pet[];
  // Per-kennel heater breakdown. Real bookings allocate specific kennels, so
  // these are populated and drive a per-kennel line item each. Quotes never
  // allocate kennels (that only happens at conversion to a booking), so
  // these are omitted there in favour of `aggregateHeaterNights` below.
  kennelSelections?: KennelSelection[];
  availableKennels?: Kennel[];
  kennelSideOptions?: KennelSideOption[];
  // Flat heater-nights count with no kennel assigned yet. Only used when
  // `kennelSelections` yields no line items - real kennel data always takes
  // priority when present. Renders as a single aggregate row instead of a
  // per-kennel breakdown.
  aggregateHeaterNights?: number;
  surcharges: Surcharge[];
  notes: string;
  status: string;
  depositAmount?: number;
  systemSettings: SystemSettings | null;
  onClose: () => void;
}

interface PetLineItem {
  petName: string;
  petType: string;
  petBreed: string;
  rateType: string;
  dailyRate: number;
  numberOfNights: number;
  groomingSubRows: Array<{ label: string; value: string }>;
  groomingCost: number;
  lineItemDiscount: number;
  lineItemDiscountPercentage: number;
  total: number;
}

interface KennelLineItem {
  kennelNumber: string;
  kennelSide: string;
  heaterNights: number;
  heaterDailyRate: number;
  total: number;
}

const BookingSummary: React.FC<BookingSummaryProps> = ({
  checkInDate,
  checkOutDate,
  numberOfDays,
  selectedPets,
  availablePets,
  kennelSelections = [],
  availableKennels = [],
  kennelSideOptions = [],
  aggregateHeaterNights = 0,
  surcharges,
  notes,
  status,
  depositAmount = 0,
  systemSettings,
  onClose,
}) => {
  const theme = useTheme();
  const VAT_RATE = 0.15;

  // Format date helper
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Calculate pet line items
  const petLineItems = useMemo(() => {
    // Pets switched off stay in selectedPets with isSelected false.
    return selectedPets
      .filter((pet) => pet.isSelected !== false)
      .map((pet) => {
        const petData = availablePets.find((p) => p.id === pet.petId);
        const petBreed = petData?.petBreed?.breed || 'Unknown';
        const numberOfNights = pet.numberOfNights || numberOfDays;

        // A pet without size data (deleted, on an existing booking) keeps
        // its stored daily rate.
        let dailyRate = 0;
        if (pet.rateType === 'Custom' && pet.customRate) {
          dailyRate = pet.customRate;
        } else if (!petData?.petSize) {
          dailyRate = pet.customRate || 0;
        } else if (pet.rateType === 'Sharing' && petData.petSize.priceSharing) {
          dailyRate = petData.petSize.priceSharing;
        } else if (petData.petSize.priceSingle) {
          dailyRate = petData.petSize.priceSingle;
        }

        // ACCOMMODATION: subject to discount
        const accommodationSubtotal = dailyRate * numberOfNights;
        const accommodationAfterDiscount =
          accommodationSubtotal - (pet.lineItemDiscount || 0);

        // GROOMING: NOT subject to discount
        const priceGroomingSmooth = petData?.petSize?.priceGroomingSmooth || 0;
        const priceGroomingLong = petData?.petSize?.priceGroomingLong || 0;
        const priceCut = petData?.petSize?.priceCut || 0;

        const groomingCost =
          (pet.groomSmooth || 0) * priceGroomingSmooth +
          (pet.groomLong || 0) * priceGroomingLong +
          (pet.groomCut || 0) * priceCut;

        // TOTAL: accommodation (after discount) + grooming (no discount)
        const total = accommodationAfterDiscount + groomingCost;

        // Grooming details - create array of grooming subrows
        const groomingSubRows: Array<{ label: string; value: string }> = [];
        if (pet.groomSmooth)
          groomingSubRows.push({
            label: 'Smooth',
            value: `${pet.groomSmooth} x R${priceGroomingSmooth.toFixed(2)}`,
          });
        if (pet.groomLong)
          groomingSubRows.push({
            label: 'Long',
            value: `${pet.groomLong} x R${priceGroomingLong.toFixed(2)}`,
          });
        if (pet.groomCut)
          groomingSubRows.push({
            label: 'Cut',
            value: `${pet.groomCut} x R${priceCut.toFixed(2)}`,
          });

        return {
          petName: pet.petName,
          petType: pet.petType,
          petBreed,
          rateType: pet.rateType || 'Single',
          dailyRate,
          numberOfNights,
          groomingSubRows,
          groomingCost,
          lineItemDiscountPercentage: pet.lineItemDiscountPercentage || 0,
          lineItemDiscount: pet.lineItemDiscount || 0,
          total,
        } as PetLineItem;
      });
  }, [selectedPets, availablePets, numberOfDays]);

  // Calculate kennel line items
  const kennelLineItems = useMemo(() => {
    const heaterRate = systemSettings?.heaterDailyRate || 0;

    return kennelSelections
      .filter((sel) => sel.kennelId !== null)
      .map((sel) => {
        const kennel = availableKennels.find((k) => k.id === sel.kennelId);
        const option = kennelSideOptions.find(
          (o) => o.id === sel.kennelSideOptionId
        );
        const displayName = option?.displayName || 'Unknown';

        // Extract kennel side prefix (e.g., "K1A" from "Kennel Standard K1A")
        const kennelSide =
          displayName.match(/[A-Z]\d[A-Z]|[A-Z]\d/g)?.[0] || displayName;

        const heaterNights = sel.heaterNights || 0;
        const total = heaterNights > 0 ? heaterRate * heaterNights : 0;

        return {
          kennelNumber: kennel?.kennelNumber?.toString() || '?',
          kennelSide,
          heaterNights,
          heaterDailyRate: heaterRate,
          total,
        } as KennelLineItem;
      });
  }, [kennelSelections, availableKennels, kennelSideOptions, systemSettings]);

  // Aggregate heater cost for callers with no kennel assigned yet (quotes).
  // Only relevant when there are no per-kennel line items - real kennel
  // data always takes priority when present.
  const aggregateHeaterTotal = useMemo(() => {
    if (kennelLineItems.length > 0 || aggregateHeaterNights <= 0) return 0;
    const heaterRate = systemSettings?.heaterDailyRate || 0;
    return heaterRate * aggregateHeaterNights;
  }, [kennelLineItems, aggregateHeaterNights, systemSettings]);

  // Calculate totals
  const totals = useMemo(() => {
    // Pet subtotal BEFORE discount
    const petSubtotalBeforeDiscount = petLineItems.reduce((sum, pet) => {
      return sum + (pet.dailyRate * pet.numberOfNights + pet.groomingCost);
    }, 0);

    // Total pet line item discounts (accommodation only)
    const totalPetLineItemDiscounts = petLineItems.reduce(
      (sum, pet) => sum + pet.lineItemDiscount,
      0
    );

    // Pet subtotal AFTER discount
    const petSubtotalAfterDiscount =
      petSubtotalBeforeDiscount - totalPetLineItemDiscounts;

    // Kennel and surcharge subtotals
    const kennelSubtotal =
      kennelLineItems.reduce((sum, kennel) => sum + kennel.total, 0) +
      aggregateHeaterTotal;
    const surchargeSubtotal = surcharges.reduce(
      (sum, surcharge) => sum + surcharge.amount,
      0
    );

    // Grand subtotal (VAT-inclusive)
    const subtotal =
      petSubtotalAfterDiscount + kennelSubtotal + surchargeSubtotal;
    const vat = Math.round((subtotal - subtotal / 1.15) * 100) / 100;
    const grandTotal = subtotal;

    return {
      petSubtotalBeforeDiscount,
      totalPetLineItemDiscounts,
      petSubtotalAfterDiscount,
      kennelSubtotal,
      surchargeSubtotal,
      subtotal,
      vat,
      grandTotal,
    };
  }, [petLineItems, kennelLineItems, surcharges, aggregateHeaterTotal]);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '800px',
        padding: '0.5rem',
        backgroundColor:
          theme.palette.mode === 'dark'
            ? theme.palette.grey[800]
            : theme.palette.grey[50],
        borderRadius: '4px',
        border: `1px solid ${theme.palette.divider}`,
        position: 'relative',
      }}
    >
      {/* Header with Close Button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem',
        }}
      >
        <Typography
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            color: 'primary.main',
            whiteSpace: 'nowrap',
          }}
        >
          {status === 'Quotation' ? 'Quote Summary' : 'Booking Summary'}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          color="error"
          sx={{
            flexShrink: 0,
            '&:hover': {
              color: 'text.primary',
              backgroundColor: 'action.hover',
            },
          }}
          title="Close summary"
        >
          <CloseIcon sx={{ fontSize: '2.5rem' }} />
        </IconButton>
      </Box>

      {/* Dates, Length & Status */}
      <Box sx={{ marginBottom: '2rem' }}>
        <SummaryRow label="Check-in" value={formatDate(checkInDate)} isBold />
        <SummaryRow label="Check-out" value={formatDate(checkOutDate)} isBold />
        <SummaryRow
          label="Length"
          value={`${numberOfDays} ${numberOfDays === 1 ? 'Day' : 'Days'}`}
          isBold
        />
        <SummaryRow label="Status" value={status} isBold />
      </Box>

      <Box
        sx={{ borderBottom: `2px dashed ${theme.palette.divider}`, mb: 2 }}
      />

      {/* Pets */}
      {petLineItems.length > 0 && (
        <Box sx={{ marginBottom: '2rem' }}>
          <Typography
            sx={{
              fontSize: '1.8rem',
              fontWeight: 700,
              marginBottom: '1rem',
              color: 'text.primary',
            }}
          >
            Pets
          </Typography>

          {petLineItems.map((pet, idx) => (
            <SummaryRow
              key={idx}
              label={pet.petName}
              value=""
              subRows={[
                {
                  label: (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '1.8rem',
                          fontWeight: 600,
                          color: 'text.primary',
                        }}
                      >
                        {pet.petType},
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '1.8rem',
                          color: 'text.primary',
                        }}
                      >
                        {pet.petBreed}
                      </Typography>
                    </Box>
                  ),
                  value: '',
                },
                {
                  label: 'Rate',
                  value: `${pet.rateType} R${pet.dailyRate.toFixed(2)} p/n`,
                },
                {
                  label: `${pet.numberOfNights} Nights`,
                  value: `R${(pet.dailyRate * pet.numberOfNights).toFixed(2)}`,
                },
                ...pet.groomingSubRows.map((row) => ({
                  label:
                    row.label === 'Smooth'
                      ? 'Groom Smooth'
                      : row.label === 'Long'
                        ? 'Groom Long'
                        : row.label,
                  value: row.value,
                })),
                ...(pet.groomingCost > 0
                  ? [
                      {
                        label: 'Grooming Cost',
                        value: `R${pet.groomingCost.toFixed(2)}`,
                      },
                    ]
                  : []),
                ...(pet.lineItemDiscount > 0
                  ? [
                      {
                        label: 'Discount',
                        value: `@ ${pet.lineItemDiscountPercentage}% = -R${pet.lineItemDiscount.toFixed(2)}`,
                      },
                    ]
                  : []),
                {
                  label: '',
                  value: `R${pet.total.toFixed(2)}`,
                },
              ]}
            />
          ))}

          <Box
            sx={{ borderBottom: `2px dashed ${theme.palette.divider}`, mb: 2 }}
          />
        </Box>
      )}

      {/* Kennels */}
      {kennelLineItems.length > 0 && (
        <Box sx={{ marginBottom: '2rem' }}>
          <Typography
            sx={{
              fontSize: '1.8rem',
              fontWeight: 700,
              marginBottom: '1rem',
              color: 'text.primary',
            }}
          >
            Kennels
          </Typography>

          {kennelLineItems.map((kennel, idx) => {
            const kennelSubRows =
              kennel.heaterNights > 0
                ? [
                    {
                      label: 'Heater Rate',
                      value: `R${kennel.heaterDailyRate.toFixed(2)}/night`,
                    },
                    {
                      label: 'Heater Nights',
                      value: kennel.heaterNights.toString(),
                    },
                    {
                      label: '',
                      value: `R${kennel.total.toFixed(2)}`,
                    },
                  ]
                : [
                    {
                      label: '',
                      value: 'No heaters',
                    },
                  ];

            return (
              <SummaryRow
                key={idx}
                label={`${kennel.kennelSide}${kennel.kennelNumber}`}
                value=""
                subRows={kennelSubRows}
              />
            );
          })}

          <Box
            sx={{ borderBottom: `2px dashed ${theme.palette.divider}`, mb: 2 }}
          />
        </Box>
      )}

      {/* Heater (aggregate) - no kennel assigned yet, e.g. a quote */}
      {kennelLineItems.length === 0 && aggregateHeaterNights > 0 && (
        <Box sx={{ marginBottom: '2rem' }}>
          <Typography
            sx={{
              fontSize: '1.8rem',
              fontWeight: 700,
              marginBottom: '1rem',
              color: 'text.primary',
            }}
          >
            Heater
          </Typography>

          <SummaryRow
            label=""
            value=""
            subRows={[
              {
                label: 'Heater Rate',
                value: `R${(systemSettings?.heaterDailyRate || 0).toFixed(2)}/night`,
              },
              {
                label: 'Heater Nights',
                value: aggregateHeaterNights.toString(),
              },
              {
                label: '',
                value: `R${aggregateHeaterTotal.toFixed(2)}`,
              },
            ]}
          />

          <Box
            sx={{ borderBottom: `2px dashed ${theme.palette.divider}`, mb: 2 }}
          />
        </Box>
      )}

      {/* Surcharges */}
      {surcharges.length > 0 && (
        <Box sx={{ marginBottom: '2rem' }}>
          <Typography
            sx={{
              fontSize: '1.8rem',
              fontWeight: 700,
              marginBottom: '1rem',
              color: 'text.primary',
            }}
          >
            Surcharges
          </Typography>

          {surcharges.map((surcharge, idx) => (
            <SummaryRow
              key={idx}
              label={surcharge.reason}
              value={`R${surcharge.amount.toFixed(2)}`}
            />
          ))}

          <Box
            sx={{ borderBottom: `2px dashed ${theme.palette.divider}`, mb: 2 }}
          />
        </Box>
      )}

      {/* Notes */}
      {notes.trim().length > 0 && (
        <Box sx={{ marginBottom: '2rem' }}>
          <Typography
            sx={{
              fontSize: '1.8rem',
              fontWeight: 700,
              marginBottom: '0.5rem',
              color: 'text.primary',
            }}
          >
            Notes
          </Typography>
          <Typography
            sx={{
              fontSize: '2rem',
              color: 'text.secondary',
              fontStyle: 'italic',
              padding: '1rem',
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.grey[700]
                  : theme.palette.grey[100],
              borderRadius: '4px',
              borderLeft: `3px solid ${theme.palette.info.main}`,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {notes}
          </Typography>

          <Box
            sx={{ borderBottom: `2px dashed ${theme.palette.divider}`, my: 2 }}
          />
        </Box>
      )}

      {/* Totals */}
      <Box sx={{ marginTop: '2rem' }}>
        {totals.petSubtotalBeforeDiscount > 0 &&
          (totals.totalPetLineItemDiscounts > 0 ? (
            <>
              <SummaryRow
                label="Pets Subtotal (before discount)"
                value={`R${totals.petSubtotalBeforeDiscount.toFixed(2)}`}
              />
              <SummaryRow
                label="Pet Line Item Discounts"
                value={`-R${totals.totalPetLineItemDiscounts.toFixed(2)}`}
              />
              <SummaryRow
                label="Pets Subtotal (after discount)"
                value={`R${totals.petSubtotalAfterDiscount.toFixed(2)}`}
              />
            </>
          ) : (
            <>
              <SummaryRow
                label="Pets Subtotal"
                value={`R${totals.petSubtotalBeforeDiscount.toFixed(2)}`}
              />
              <SummaryRow label="No discount" value="" />
            </>
          ))}

        {totals.kennelSubtotal > 0 && (
          <SummaryRow
            label={
              kennelLineItems.length > 0
                ? 'Kennels Subtotal'
                : 'Heater Subtotal'
            }
            value={`R${totals.kennelSubtotal.toFixed(2)}`}
          />
        )}

        {totals.surchargeSubtotal > 0 && (
          <SummaryRow
            label="Surcharges Subtotal"
            value={`R${totals.surchargeSubtotal.toFixed(2)}`}
          />
        )}

        <Box
          sx={{ borderBottom: `2px solid ${theme.palette.divider}`, my: 1.5 }}
        />

        <SummaryRow
          label="Subtotal"
          value={`R${totals.subtotal.toFixed(2)}`}
          isBold
        />
        <SummaryRow
          label={`VAT incl. (${(VAT_RATE * 100).toFixed(0)}%)`}
          value={`R${totals.vat.toFixed(2)}`}
          isBold
        />

        <Box
          sx={{
            borderBottom: `2px solid ${theme.palette.primary.main}`,
            my: 1.5,
          }}
        />

        <SummaryRow
          label="TOTAL"
          value={`R${totals.grandTotal.toFixed(2)}`}
          isTotal
        />

        {status !== 'Quotation' && (depositAmount ?? 0) > 0 && (
          <>
            <SummaryRow
              label="Deposit Paid"
              value={`-R${(depositAmount ?? 0).toFixed(2)}`}
              isBold
            />
            {(depositAmount ?? 0) < totals.grandTotal && (
              <SummaryRow
                label="Still Owing"
                value={`R${(totals.grandTotal - (depositAmount ?? 0)).toFixed(2)}`}
                isTotal
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default BookingSummary;
